import React, { useEffect, useState } from "react";
import { Button, Input, Space, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config";

const { Text } = Typography;

/** @typedef {{ id: string; title: string; users?: Array<{ userID?: string } | string> }} ExamBankAssignmentItem */
/** @typedef {{ userId: string; examBankIds: string[] }} AssignUserToExamBankRequest */
/** @typedef {{ success?: boolean; message?: string }} AssignUserToExamBankResponse */

const getBankId = (bank) => bank.id ?? bank.Id ?? bank.examBankId;

const getBankUsers = (bank) =>
    bank.users ?? bank.Users ?? bank.userIds ?? bank.UserIds ?? bank.userIDs ?? [];

const isAssignedToUser = (bank, userId) =>
    getBankUsers(bank).some((user) => {
        const id =
            typeof user === "object"
                ? user.userID ?? user.userId ?? user.id ?? user.Id
                : user;
        return String(id) === String(userId);
    });

const extractBanks = (payload) => {
    const data = payload?.data ?? payload;
    return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
};

const extractTotal = (payload, fallback) => {
    const data = payload?.data ?? payload;
    return data?.count ?? payload?.count ?? fallback;
};

const SelectExamBankModal = ({ object, onSuccess }) => {
    const userId = object?.userID;
    const [banks, setBanks] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedMap, setSelectedMap] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 8, total: 0 });

    const fetchBanks = async (page = 1, pageSize = pagination.pageSize, title = search) => {
        if (!userId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${config.apiBaseUrl}/api/ExamBank/GetList`,
                {
                    Page: page,
                    PageSize: pageSize,
                    Filter: title ? `title=*${title}/i` : "",
                    OrderBy: "",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const fetchedBanks = extractBanks(response.data);
            const assignedBanks = fetchedBanks.filter((bank) => isAssignedToUser(bank, userId));

            setBanks(fetchedBanks);
            setSelectedRowKeys((previous) =>
                Array.from(new Set([...previous, ...assignedBanks.map(getBankId).filter(Boolean)]))
            );
            setSelectedMap((previous) => {
                const next = new Map(previous);
                assignedBanks.forEach((bank) => next.set(getBankId(bank), bank));
                return next;
            });
            setPagination({ current: page, pageSize, total: extractTotal(response.data, fetchedBanks.length) });
        } catch (error) {
            console.error("Error fetching exam banks:", error);
            toast.error(<p dir="rtl" className="font-custom">بارگذاری بانک‌های آزمون با خطا مواجه شد</p>);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
    }, []);

    const handleSelectionChange = (nextKeys, selectedRows) => {
        setSelectedRowKeys(nextKeys);
        setSelectedMap((previous) => {
            const next = new Map(previous);
            selectedRows.forEach((bank) => next.set(getBankId(bank), bank));
            Array.from(next.keys()).forEach((id) => {
                if (!nextKeys.includes(id)) next.delete(id);
            });
            return next;
        });
        setHasChanges(true);
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const response = await axios.post(
                `${config.apiBaseUrl}/api/exambank/AssignUserToExamBank`,
                /** @type {AssignUserToExamBankRequest} */
                { userId, examBankIds: selectedRowKeys },
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            /** @type {AssignUserToExamBankResponse} */
            const result = response.data;
            if (result?.success === false) {
                throw new Error(result?.message || "Assignment failed");
            }

            setHasChanges(false);
            toast.success(<p dir="rtl" className="font-custom">دسترسی به بانک‌های آزمون به‌روزرسانی شد</p>);
            onSuccess?.();
        } catch (error) {
            console.error("Error assigning exam banks:", error);
            toast.error(
                <p dir="rtl" className="font-custom">
                    {error.response?.data?.message || "به‌روزرسانی دسترسی بانک‌های آزمون با خطا مواجه شد"}
                </p>
            );
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: "عنوان بانک آزمون",
            dataIndex: "title",
            key: "title",
            align: "right",
            className: "font-custom",
            render: (_, bank) => <Text strong>{bank.title ?? bank.Title ?? "—"}</Text>,
        },
        {
            title: "تعداد آزمون‌ها",
            key: "examCount",
            align: "center",
            className: "font-custom",
            render: (_, bank) => bank.examCount ?? bank.ExamCount ?? bank.exams?.length ?? bank.Exams?.length ?? 0,
        },
    ];

    return (
        <div className="font-custom">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Space.Compact style={{ width: "100%" }}>
                    <Input
                        value={search}
                        allowClear
                        placeholder="جستجوی عنوان بانک آزمون"
                        onChange={(event) => setSearch(event.target.value)}
                        onPressEnter={() => fetchBanks(1, pagination.pageSize)}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchBanks(1, pagination.pageSize)} />
                </Space.Compact>
                <Table
                    rowKey={getBankId}
                    columns={columns}
                    dataSource={banks}
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: handleSelectionChange,
                        preserveSelectedRowKeys: true,
                    }}
                    pagination={{
                        ...pagination,
                        onChange: (page, pageSize) => fetchBanks(page, pageSize),
                    }}
                />
                <div className="flex items-center justify-between">
                    <Text type="success">تعداد بانک انتخاب‌شده: {selectedMap.size}</Text>
                    <Button type="primary" onClick={handleSubmit} loading={saving} disabled={!hasChanges}>
                        ثبت تغییرات
                    </Button>
                </div>
            </Space>
        </div>
    );
};

export default SelectExamBankModal;
